import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Music, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-[0_10px_60px_rgba(44,53,49,0.06)] border border-[#EBE7E0] p-8 sm:p-10 w-full max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-[#B86666] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="text-xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>Lien invalide</h1>
          <p className="text-sm text-[#6A7771] mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/forgot-password" className="text-[#6B8576] font-medium text-sm hover:underline" data-testid="reset-retry-link">Faire une nouvelle demande</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="bg-white rounded-3xl shadow-[0_10px_60px_rgba(44,53,49,0.06)] border border-[#EBE7E0] p-8 sm:p-10 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#6B8576] rounded-3xl flex items-center justify-center mx-auto mb-5">
            <Music className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Nouveau mot de passe</h1>
        </div>

        {success ? (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle className="w-12 h-12 text-[#6B8576] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#2C3531] font-medium mb-2">Mot de passe réinitialisé !</p>
            <p className="text-sm text-[#6A7771] mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
            <Link to="/login" className="inline-block px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300" data-testid="reset-login-link">Se connecter</Link>
          </div>
        ) : (
          <>
            {error && <div className="bg-red-50 text-[#B86666] p-3.5 rounded-2xl text-sm mb-5 border border-red-100" data-testid="reset-error">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Nouveau mot de passe</label>
                <input type="password" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300" value={password} onChange={e => setPassword(e.target.value)} required data-testid="reset-password" placeholder="Minimum 6 caractères" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-1.5">Confirmer le mot de passe</label>
                <input type="password" className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="reset-confirm" placeholder="Retapez le mot de passe" />
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 disabled:opacity-50 text-sm" data-testid="reset-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Réinitialiser'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
